import { useEffect } from "react";
import styled from "styled-components";

import { useCustomerPortal } from "../../context/CustomerPortalContext";
import CustomerHero from "../../components/customer/CustomerHero";
import CustomerNavigation from "../../components/customer/CustomerNavigation";
import CustomerCard from "../../components/customer/CustomerCard";
import CustomerInfoRow from "../../components/customer/CustomerInfoRow";

export default function CustomerProfilePage() {
  const {
    currentCustomer,
    authLoading,
    error,
    fetchCustomer,
    logout,
  } = useCustomerPortal();

  useEffect(() => {
    if (!currentCustomer) {
      fetchCustomer();
    }
  }, [currentCustomer, fetchCustomer]);

  if (authLoading) {
    return (
      <PageWrapper>
        <PanelInfo>Chargement du profil client...</PanelInfo>
      </PageWrapper>
    );
  }

  if (error && !currentCustomer) {
    return (
      <PageWrapper>
        <PanelMessage $error>{error}</PanelMessage>
      </PageWrapper>
    );
  }

  const identifiers = currentCustomer?.player_identifiers || [];

  return (
    <PageWrapper>
      <CustomerPanel>
        <CustomerHero customer={currentCustomer} onLogout={logout} />

        <PortalLayout>
          <CustomerNavigation />

          <ContentArea>
            <Grid>
              <CustomerCard title="Identité">
                <InfoList>
                  <CustomerInfoRow label="Nom complet" value={currentCustomer?.name} />
                  <CustomerInfoRow label="Prénom" value={currentCustomer?.firstname} />
                  <CustomerInfoRow label="Nom" value={currentCustomer?.lastname} />
                  <CustomerInfoRow label="Date de naissance" value={currentCustomer?.date_of_birth} />
                  <CustomerInfoRow label="UUID client" value={currentCustomer?.uuid} mono />
                </InfoList>
              </CustomerCard>

              <CustomerCard title="Coordonnées">
                <InfoList>
                  <CustomerInfoRow label="E-mail" value={currentCustomer?.email} />
                  <CustomerInfoRow label="Téléphone" value={currentCustomer?.phone_number} />
                  <CustomerInfoRow label="Adresse" value={currentCustomer?.address} />
                  <CustomerInfoRow label="Code postal" value={currentCustomer?.postal_code} />
                  <CustomerInfoRow label="Ville" value={currentCustomer?.city} />
                  <CustomerInfoRow label="Pays" value={currentCustomer?.country} />
                </InfoList>
              </CustomerCard>

              <CustomerCard title="Préférences">
                <PreferenceGrid>
                  <PreferenceItem $active={currentCustomer?.marketing_opt_in}>
                    <strong>Marketing</strong>
                    <span>
                      {currentCustomer?.marketing_opt_in ? "Accepté" : "Refusé"}
                    </span>
                  </PreferenceItem>

                  <PreferenceItem $active={currentCustomer?.newsletter_opt_in}>
                    <strong>Newsletter</strong>
                    <span>
                      {currentCustomer?.newsletter_opt_in ? "Acceptée" : "Refusée"}
                    </span>
                  </PreferenceItem>
                </PreferenceGrid>

                <MutedText>
                  La modification des préférences sera disponible dès que
                  l’endpoint de mise à jour du profil client sera exposé.
                </MutedText>
              </CustomerCard>

              <CustomerCard title="Comptes joueurs">
                {identifiers.length > 0 ? (
                  <IdentifierList>
                    {identifiers.map((identifier) => (
                      <IdentifierItem key={identifier.uuid || identifier.identifier_key}>
                        <strong>
                          {identifier.identifier_label ||
                            identifier.label ||
                            identifier.identifier_key}
                        </strong>
                        <span>
                          {identifier.player_number ||
                            identifier.number ||
                            "Numéro non renseigné"}
                        </span>
                      </IdentifierItem>
                    ))}
                  </IdentifierList>
                ) : (
                  <EmptyState>
                    Aucun identifiant joueur n’est encore rattaché à ce profil.
                  </EmptyState>
                )}
              </CustomerCard>

              <CustomerCard title="Sécurité du compte" full>
                <SecurityGrid>
                  <SecurityItem>
                    <strong>Mot de passe</strong>
                    <span>
                      Utilisez le flux “mot de passe oublié” pour réinitialiser
                      votre accès client.
                    </span>
                  </SecurityItem>

                  <SecurityItem>
                    <strong>Session</strong>
                    <span>
                      Votre session client est protégée par cookies sécurisés et
                      jetons CSRF.
                    </span>
                  </SecurityItem>
                </SecurityGrid>
              </CustomerCard>
            </Grid>
          </ContentArea>
        </PortalLayout>
      </CustomerPanel>
    </PageWrapper>
  );
}

const PageWrapper = styled.main`
  min-height: 100dvh;
  width: 100vw;
  padding: 1.5rem;
  background: #0b1328;
  box-sizing: border-box;
  overflow-y: auto;
`;

const CustomerPanel = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  color: #f7f7f7;
`;

const PortalLayout = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentArea = styled.div`
  min-width: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InfoList = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const PreferenceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const PreferenceItem = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: ${({ $active }) =>
    $active ? "rgba(255, 249, 234, 0.16)" : "#202a43"};

  strong {
    display: block;
    font-weight: 950;
    margin-bottom: 0.35rem;
  }

  span {
    color: #fff9ea;
    opacity: 0.76;
  }
`;

const IdentifierList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const IdentifierItem = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;

  strong {
    display: block;
    font-weight: 950;
    margin-bottom: 0.35rem;
  }

  span {
    opacity: 0.72;
  }
`;

const SecurityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const SecurityItem = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;

  strong {
    display: block;
    font-weight: 950;
    margin-bottom: 0.35rem;
  }

  span {
    opacity: 0.72;
    line-height: 1.5;
  }
`;

const MutedText = styled.p`
  margin: 0;
  opacity: 0.72;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  opacity: 0.75;
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;

const PanelInfo = styled.div`
  padding: 1rem;
  color: #f7f7f7;
  opacity: 0.75;
`;
